import { useMemo } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { FilterContextValue } from "@/contexts/FilterContext";
import {
  isFTSEntityType,
  useFTSSearchEnhancer,
} from "@/lib/hooks/api/search/use-fts-search-enhancer";

interface UseFTSEnhancementProps<T extends BaseEntity> {
  enableFTS: boolean;
  entityName: string;
  filterContext: FilterContextValue<T> | null | undefined;
  filteredData: T[];
}

/**
 * Handles full-text search enhancement
 * Applies FTS when enabled and search term is present
 */
export function useFTSEnhancement<T extends BaseEntity>({
  enableFTS,
  entityName,
  filterContext,
  filteredData,
}: UseFTSEnhancementProps<T>) {
  // Define searchable fields based on entity type
  const searchableFields = useMemo(() => {
    const fieldMap: Record<string, string[]> = {
      contacts: ["name", "email", "company_name", "phone"],
      companies: ["name", "website", "industry", "phone"],
      deals: ["title", "description", "company_name"],
      tickets: ["title", "description", "subject"],
      tasks: ["title", "description"],
      communications: ["title", "body", "subject"],
      offerings: [
        "title",
        "description",
        "executive_summary",
        "tags",
        "pricing_display",
        "cta_text",
        "linked_project_slug",
      ],
      "recurring-transactions": ["item", "description", "contact_name", "company_name"],
      "recurring-charges": ["customer_id", "admin_email", "schedule_type"],
      admin_users: ["first_name", "last_name", "email"],
      social_media_posts: ["title", "body", "tags", "location"],
    };
    return fieldMap[entityName] || [];
  }, [entityName]);

  // Apply FTS enhancement if enabled
  const {
    data: enhancedData,
    totalCount: ftsTotalCount,
    isLoading: ftsIsLoading,
    error: ftsError,
  } = useFTSSearchEnhancer({
    enableFTS: enableFTS && searchableFields.length > 0,
    entityType: isFTSEntityType(entityName) ? entityName : undefined,
    searchTerm: filterContext?.filters.search || "",
    data: filteredData,
    searchableFields,
  });

  // Use enhanced data if FTS is enabled, otherwise use filtered data
  const finalData = enableFTS ? enhancedData : filteredData;

  // Use FTS-enhanced data if available, otherwise use filtered data
  // This ensures search results are displayed in the table
  const processedData =
    enableFTS && finalData && (filterContext?.filters.search || "").trim().length > 0
      ? finalData
      : filteredData;

  return {
    enhancedData,
    ftsTotalCount,
    ftsIsLoading,
    ftsError,
    finalData,
    processedData,
  };
}
